import React from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { AgentFormValues, SectionCard, inputCls } from './agentShared';
import { cn } from '@/utils/cn';
import PromptBlock from './PromptBlock';

interface InstructionsTabProps {
  isEdit: boolean;
  onPreview: () => void;
  previewing: boolean;
}

const InstructionsTab: React.FC<InstructionsTabProps> = ({ isEdit, onPreview, previewing }) => {
  const { control, register } = useFormContext<AgentFormValues>();

  const { fields, append, remove, move } = useFieldArray({
    control,
    name: 'system_prompts',
  });

  return (
    <>
      {/* Audio prompt — prepended to the composed system prompt on voice turns only
          (realtime + push-to-talk). Left blank → nothing is prepended. */}
      <SectionCard
        title="Audio prompt"
        desc="Prepended to the system prompt on voice turns only (realtime + push-to-talk). Use this for spoken-reply rules: brevity, no re-greeting, no filler openers. Leave blank to inject nothing."
      >
        <textarea
          rows={6}
          placeholder="e.g. Reply as natural, casual spoken conversation. One short sentence default. Never re-greet after the first turn. No filler openers (nice / great / got it)."
          className={cn(inputCls, 'font-mono text-sm leading-relaxed')}
          {...register('audio_prompt')}
        />
      </SectionCard>

      {/* System prompts */}
      <SectionCard
        title="System prompts"
        desc="Each block is injected in order. Tag blocks by purpose and add conditions to target specific roles or contexts."
        right={
          isEdit ? (
            <button
              type="button"
              onClick={onPreview}
              disabled={previewing}
              className="text-xs text-brand-dark border border-brand-light px-2.5 py-1 rounded hover:bg-brand-light/50 transition disabled:opacity-50"
            >
              {previewing ? 'Loading…' : 'Preview compiled prompt'}
            </button>
          ) : undefined
        }
      >
        {/* Legend */}
        <div className="mb-4 flex flex-wrap gap-x-4 gap-y-1.5">
          {[
            { label: 'Persona',  dot: 'bg-purple-500',  desc: 'Identity & tone' },
            { label: 'Rules',    dot: 'bg-red-500',     desc: 'Guardrails' },
            { label: 'Context',  dot: 'bg-blue-500',    desc: 'Domain knowledge' },
            { label: 'Format',   dot: 'bg-emerald-500', desc: 'Response style' },
            { label: 'Tools',    dot: 'bg-amber-500',   desc: 'Tool usage' },
          ].map((c) => (
            <span key={c.label} className="inline-flex items-center gap-1.5 text-xs text-ui-text3">
              <span className={`h-2 w-2 rounded-full ${c.dot}`} />
              <span className="font-medium text-ui-text2">{c.label}</span>
              <span>— {c.desc}</span>
            </span>
          ))}
        </div>

        <div className="space-y-3">
          {fields.length === 0 && (
            <p className="py-4 text-center text-xs text-ui-text3">
              No prompt blocks yet. Add one below.
            </p>
          )}

          {fields.map((field, i) => (
            <PromptBlock
              key={field.id}
              index={i}
              total={fields.length}
              onMove={(dir) => move(i, i + dir)}
              onRemove={() => remove(i)}
            />
          ))}

          <button
            type="button"
            onClick={() => append({ title: '', prompt: '' })}
            className="text-sm text-brand-dark font-medium hover:underline"
          >
            + Add prompt block
          </button>
        </div>
      </SectionCard>

    </>
  );
};

export default InstructionsTab;
